import React, { useContext, FunctionComponent, useState } from 'react';
import { useTranslation } from 'react-i18next';

// MUI Compoenents
import Toolbar from '@mui/material/Toolbar';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';

// Custom Components
import useFormController, { SubmodelAPIPaths } from '@/components/useFormController';
import FormContent from '@/components/FormContent';
import DynamicDialog from '@/ui/Dialog/DynamicDialog';
import DialogToolbarHeading from '@/ui/Dialog/DialogToolbarHeading';
import DialogToolbarButtonClose from '@/ui/Dialog/ToolbarButtons/DialogToolbarButtonClose';
import ToolbarFillContent from '@/ui/Toolbar/ToolbarFillContent';
import DialogActionButtonAdd from '@/ui/Dialog/ActionButtons/DialogActionButtonAdd';
import DialogActionButtonSave from '@/ui/Dialog/ActionButtons/DialogActionButtonSave';
import { GridContainer } from '@/ui/Grid';
import DialogActionButtonCancel from '@/ui/Dialog/ActionButtons/DialogActionButtonCancel';

// Contexts
import DialogContext, { IDialogProps } from '@/context/DialogContext/DialogContext';
import SnackbarContext from '@/context/SnackbarContext/SnackbarContext';

// Types
import { ClosingDetails } from '@/@types/components/formController';

const MapLayersDialog: FunctionComponent<IDialogProps> = (props) => {
  const { hideDialog } = useContext(DialogContext);
  const { showNotification } = useContext(SnackbarContext);
  const { t } = useTranslation();

  const { dc, mode, form, dataId, onClose, initialRecord } = props;

  const [submodelAPIPath, setSubmodelAPIPath] = useState<SubmodelAPIPaths | undefined>();

  const { record, validation, fields, pickerDataControllers, onFieldChange, doInsert, doUpdate, doClose } = useFormController({
    mode,
    recordId: dataId,
    form,
    dc,
    onClose,
    initialRecord,
    alternativeSubmodelAPIPath: submodelAPIPath,
  });

  const handleFieldChange = (value: any, source: string) => {
    if (source === 'module_id') {
      if (value && value !== null && value.value && value.value !== null) {
        const moduleId = value.value;
        if (typeof onFieldChange === 'function') {
          onFieldChange(null, 'app_permission_id');
        }
        setSubmodelAPIPath({
          module_id: 'system/app_modules?view=picker',
          app_permission_id: `system/app_modules/${moduleId}/app_permissions?view=picker`,
        });
      }
    } else {
      setSubmodelAPIPath({
        module_id: 'system/app_modules?view=picker',
        app_permission_id: `system/app_modules/0/app_permissions?view=picker`,
      });
      if (typeof onFieldChange === 'function') {
        onFieldChange(null, 'app_permission_id');
      }
    }

    if (onFieldChange) {
      onFieldChange(value, source);
    }
  };

  const handleInsert = () => {
    if (doInsert) {
      doInsert()
        .then((result) => {
          if (result.success) {
            const msg = 'messages.insert_map_layer_success';
            showNotification(msg, 'success');
            close({ dataChanged: true, action: 'insert', id: result.id });
          } else if (result.error) {
            const msg = 'error.insert_map_layer';
            showNotification(msg, 'error');
          } else if (result.validationPass === false) {
            // do nothing
          }
        })
        .catch((resp) => {
          showNotification(resp.error, 'error');
        });
    }
  };

  const handleUpdate = () => {
    if (doUpdate) {
      doUpdate()
        .then((result) => {
          if (result.success) {
            const msg = 'messages.update_map_layer_success';
            showNotification(msg, 'success');
            close({ dataChanged: true, action: 'update' });
          } else if (result.error) {
            const msg = 'error.update_map_layer';
            showNotification(msg, 'error');
          } else if (result.validationPass === false) {
            // do nothing
          }
        })
        .catch((resp) => {
          showNotification(resp.error, 'error');
        });
    }
  };

  const handleClose = () => {
    if (doClose) {
      doClose().then((result) => {
        if (result.success) {
          close({ dataChanged: false, action: 'cancel' });
        }
      });
    }
  };

  const close = (result: ClosingDetails) => {
    onClose(result);
    hideDialog();
  };

  let cmode = mode;
  if (record.status === 30) {
    cmode = 'view';
  }

  return (
    <DynamicDialog open maxWidth="sm" onClose={handleClose}>
      <DialogTitle>
        <Toolbar variant="dense" disableGutters>
          <DialogToolbarHeading>{t('admin:titles.map_layers')}</DialogToolbarHeading>
          <ToolbarFillContent />
          <DialogToolbarButtonClose onClick={handleClose} />
        </Toolbar>
      </DialogTitle>
      <DialogContent>
        <GridContainer direction="row" spacing={2} my={2}>
          <FormContent
            title={t('admin:titles.map_layer_information') as string}
            fieldNames={[
              'ui_code',
              'ui_title',
              'ui_ttoken',
              'map_id',
              'tenant_id',
              'gfi_model_id',
              'gs_layer_source',
              'gs_layer_type',
              'gs_layer',
              'gs_format',
              'ui_legend',
              'legend_conf_id',
              'gs_url',
              'gs_projection',
              'layer_functionality_id',
              'module_id',
              'app_permission_id',
            ]}
            record={record}
            validation={validation}
            fields={fields}
            onFieldChange={handleFieldChange}
            mode={cmode}
            columns={2}
            pickerDataControllers={pickerDataControllers}
          />
        </GridContainer>
      </DialogContent>
      <DialogActions>
        <DialogActionButtonCancel variant="outlined" onClick={handleClose} />
        {mode === 'insert' ? (
          <DialogActionButtonAdd variant="contained" onClick={handleInsert} />
        ) : mode === 'update' ? (
          <DialogActionButtonSave variant="contained" onClick={handleUpdate} />
        ) : null}
      </DialogActions>
    </DynamicDialog>
  );
};

export default MapLayersDialog;
